import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchBusinessDefaults, fetchRentalTotal } from './thunks'
import type { ChargeableItem } from '../services/types'
import type { RootState } from '..'
import { parseMoneyString } from './utils'
import { BespokeField } from '@view/components/Reservations/orderDetails/types'
import { updateCurrentlyEditing, updateInsuranceType } from './resEditFormSlice'
import { setLocationDefaults, setOrderDetailsDefaults } from './resDefaultsSlice'
import { setPoolFleetVehicles, setSelectedPoolFleetVehicle, setVehicleGroup } from './resVehicleSlice'
import { updateAddress, updateSameAddress } from './resLocationsSlice'

type InitialState = {
	total: null | string
	rate: null | string
	chargeableItems: { item: { id: string; value: string; text: string }; rates: { rate: string; id: string; num: number; net: string } }[]
	mandatoryFields: {
		orderInputMandatory?: { clientReference: string; costCentre: string; purchaseOrder: string }
		approver?: { approver: number }
		driverLicence?: { hasLicence: boolean }
		bespokeFieldMandatory?: { [key: string]: string }
	}
	locationAndTimeMandatoryFields: { name: string; key: string }[]
}

type Chargeable = { id: string; net: string; num: number; total?: string }
export const createChargeableItem = ({ id, net, num }: Chargeable) => {
	const total = net ? parseMoneyString(net) * num : 0
	return {
		id,
		net,
		num,
		total,
	}
}
const locationAndTimeMandatoryFields = [
	{
		name: 'Start Date',
		key: 'startDate',
	},
	{
		name: 'End Date',
		key: 'endDate',
	},
	{
		name: 'Delivery Address',
		key: 'deliveryAddress',
	},
	{
		name: 'Collection Address',
		key: 'collectionAddress',
	},
	{
		name: 'Pick Up Type',
		key: 'pickupType',
	},
]

const initialState = {
	total: null,
	chargeableItems: [],
	mandatoryFields: { orderInputMandatory: { tariffBandId: null }, bespokeFieldMandatory: {} },
	locationAndTimeMandatoryFields,
	totalBulkBooking: '1',
	poolFleetView: false,
	poolFleetDataLoading: false,
	backLinkClicked: false,
	taxiDataLoading: false,
	displayBackToRentalButton: false,
	resetTaxiConfirmation: false,
	taxiModalVisible: false,
} as InitialState

export const resUiSlice = createSlice({
	name: 'resUi',
	initialState,
	reducers: {
		setBackLinkClicked(state, action) {
			state.backLinkClicked = action.payload
			state.resetTaxiConfirmation = true
		},
		setTotalBulkBooking(state, action) {
			state.totalBulkBooking = action.payload
		},
		setFodRideOrderInputMandatoryFields(state, _action) {
			state.mandatoryFields.orderInputMandatory = {}
		},
		initChargeableItems(state, action) {
			// pass through items as new chargeableItems
			// if item is in array skip
			if (!state.chargeableItems.length) {
				state.chargeableItems = action.payload.map(createChargeableItem)
			} else {
				const cleanPayload = action.payload
					.filter((item: ChargeableItem) => {
						return state.chargeableItems.find(addItem => addItem.id !== item.id)
					})
					.map(createChargeableItem)
				const ids = action.payload.map((item: ChargeableItem) => ({ id: item.id }))
				const filteredItems = state.chargeableItems.filter((item: Chargeable) => ids.find((id: string) => id === item.id))
				state.chargeableItems = [...cleanPayload, ...filteredItems]
			}
		},
		updateChargeableItems(state, action) {
			state.chargeableItems = action.payload
		},
		setBespokeMandatoryFields(state, action: PayloadAction<BespokeField[]>) {
			const bespokeMandatoryFields = action.payload.filter(field => field.mandatory === '1')
			const bespokeFieldsWithNoValue = bespokeMandatoryFields.reduce((accum, field) => {
				accum = {
					...accum,
					[field.id]: state.mandatoryFields?.bespokeFieldMandatory?.[field.id]
						? state.mandatoryFields?.bespokeFieldMandatory?.[field.id]
						: '',
				}

				return accum
			}, {})

			state.mandatoryFields.bespokeFieldMandatory = {
				...bespokeFieldsWithNoValue,
			}
		},
		setBespokeMandatoryFieldsValues(state, action: PayloadAction<{ name: string; value: string; mandatory: boolean }>) {
			const { name, value, mandatory } = action.payload

			if (mandatory) {
				state.mandatoryFields.bespokeFieldMandatory = {
					...state.mandatoryFields.bespokeFieldMandatory,
					[name]: value,
				}
			}
		},
		setOrderInputMandatoryFieldsValues(state, action: PayloadAction<{ name: string; value: string | number }>) {
			const { name, value } = action.payload

			state.mandatoryFields.orderInputMandatory = {
				...state.mandatoryFields.orderInputMandatory,
				[name]: value,
			}
		},
		setMandatoryApprover(state, action: PayloadAction<number | null>) {
			state.mandatoryFields.approver = {
				approver: action.payload,
			}
		},
		setHasMandatoryLicence(state, action: PayloadAction<boolean | null>) {
			state.mandatoryFields.driverLicence = {
				hasLicence: action.payload,
			}
		},
		setApproval(state, action) {
			if (action.payload) {
				state.mandatoryFields.orderInputMandatory = { ...state.mandatoryFields.orderInputMandatory, approvalEmailAddress: '' }
			}
		},
		setPoolFleetView(state, action) {
			const { visible, isLoading } = action.payload
			state.poolFleetView = visible
			state.poolFleetDataLoading = isLoading
		},
		setTaxiDataLoading(state, action) {
			state.taxiDataLoading = action.payload
		},
		setDisplayBackToRentalButton(state, action) {
			state.displayBackToRentalButton = action.payload

			if (!action.payload) {
				state.resetTaxiConfirmation = true
			}
		},
		setTaxiModalVisible(state, action) {
			state.taxiModalVisible = action.payload
		},
	},
	extraReducers: builder => {
		builder.addCase(updateAddress, (state, _action) => {
			state.backLinkClicked = false
			state.resetTaxiConfirmation = false
			state.taxiModalVisible = true
		}),
			builder.addCase(updateSameAddress, (state, _action) => {
				state.backLinkClicked = false
			}),
			builder.addCase(setPoolFleetVehicles, (state, action) => {
				if (!action.payload) {
					state.locationAndTimeMandatoryFields = state.locationAndTimeMandatoryFields.filter(
						field => field.key != 'rentalVehicleId'
					)
					return
				}

				const poolFleetExists = state.locationAndTimeMandatoryFields.find(field => field.key == 'rentalVehicleId')

				if (poolFleetExists) return

				state.locationAndTimeMandatoryFields = [
					...state.locationAndTimeMandatoryFields,
					{ name: 'Pool Fleet Vehicle', key: 'rentalVehicleId' },
				]
			}),
			builder.addCase(setLocationDefaults, (state, action) => {
				const { reasonForHireRequired } = action.payload

				if (reasonForHireRequired) {
					state.mandatoryFields.orderInputMandatory.reasonForHire = null
				}
			})
		builder.addCase(fetchRentalTotal.fulfilled, (state, action) => {
			state.total = action.payload.total
			state.rate = action.payload.rate
		})
		builder.addCase(updateCurrentlyEditing, (_, action) => {
			if (action.payload === false) {
				return initialState
			}
		})
		builder.addCase(setSelectedPoolFleetVehicle, state => {
			delete state.mandatoryFields.orderInputMandatory.driversInsuranceExpiry
		})
		builder.addCase(setVehicleGroup, state => {
			delete state.mandatoryFields.orderInputMandatory.driversInsuranceExpiry
		})
		builder.addCase(updateInsuranceType, (state, action) => {
			if (action.payload == 'COI' || action.payload == 'CDW') {
				delete state.mandatoryFields.orderInputMandatory.driversInsuranceExpiry
			}

			if (action.payload == 'Drivers') {
				state.mandatoryFields.orderInputMandatory.driversInsuranceExpiry = ''
			}

			state.insuranceTypeText = action.payload
		})
		builder.addCase(setOrderDetailsDefaults, (state, action) => {
			const { customerOrderNumberRequired, checkValidCostCentres, orderNumberFormat, insuranceType } = action.payload

			state.insuranceTypeText = insuranceType

			if (insuranceType == 'Drivers') {
				state.mandatoryFields.orderInputMandatory.driversInsuranceExpiry = ''
			}

			const inputFields = {
				customerOrderNumberRequired,
				checkValidCostCentres,
			}

			const inputFieldKeys = {
				customerOrderNumberRequired: 'clientReference',
				checkValidCostCentres: 'costCentre',
			}

			function setMandatoryOrderInputFields(inputFields: { [key: string]: string }, inputFieldsKeys: { [key: string]: string }) {
				const mandatoryOrderInputFields: { key: string } | {} = {}

				for (const [key, value] of Object.entries(inputFields)) {
					if (Number(value)) {
						mandatoryOrderInputFields[inputFieldsKeys[key]] =
							key == 'customerOrderNumberRequired' ? orderNumberFormat || '' : ''
					}
				}

				return mandatoryOrderInputFields
			}

			state.mandatoryFields.orderInputMandatory = {
				...state.mandatoryFields.orderInputMandatory,
				...setMandatoryOrderInputFields(inputFields, inputFieldKeys),
			}
		})
		builder.addCase(fetchBusinessDefaults.fulfilled, (state, action) => {
			const { hideCustomerOrderNumber, hidePurchaseOrderNumber, hideCostCentre } = action.payload

			const inputFields = {
				hideCustomerOrderNumber,
				hidePurchaseOrderNumber,
				hideCostCentre,
			}

			const inputFieldKeys = {
				hideCustomerOrderNumber: 'clientReference',
				hidePurchaseOrderNumber: 'purchaseOrder',
				hideCostCentre: 'costCentre',
			}

			function setMandatoryOrderInputFields(inputFields: { [key: string]: string }, inputFieldsKeys: { [key: string]: string }) {
				const mandatoryOrderInputFields: { key: string } | {} = { tariffBandId: null }

				for (const [key, value] of Object.entries(inputFields)) {
					if (value == 'show') {
						mandatoryOrderInputFields[inputFieldsKeys[key]] = ''
					}
				}

				return mandatoryOrderInputFields
			}

			state.mandatoryFields.orderInputMandatory = setMandatoryOrderInputFields(inputFields, inputFieldKeys)
		})
	},
})

export const resUiSliceReducer = resUiSlice.reducer

export const {
	updateChargeableItems,
	initChargeableItems,
	setBespokeMandatoryFields,
	setBespokeMandatoryFieldsValues,
	setOrderInputMandatoryFields,
	setOrderInputMandatoryFieldsValues,
	setMandatoryApprover,
	setHasMandatoryLicence,
	setApproval,
	setFodRideOrderInputMandatoryFields,
	setTotalBulkBooking,
	setPoolFleetView,
	setBackLinkClicked,
	setTaxiDataLoading,
	setDisplayBackToRentalButton,
	setTaxiModalVisible,
} = resUiSlice.actions
export const selectResUi = (state: RootState) => state.resUi
